import { LocaleFormat } from './locale-format';
import { Organization, SalesforceUser } from './organization';

export type TokenPayload = {
    user: string;
    lastLogin: Date;
};

export type LinkedOrganization = {
    organization: string | Organization;
    role: string;
    active: boolean;
    isWhiteLabeled?: boolean;
    type: string;
};

export type UserGeneralNotifications = {
    newMessage: boolean;
    orderReceivedByLab?: boolean;
    orderDeclinedByLab?: boolean;
    orderSentByLab?: boolean;
    deliveryDateChanged: boolean;
    orderNotReceived: {
        active: boolean;
        days: number;
    };
};

export type UserToken = {
    token: string;
    user: User;
};

export type DentistNotes = {
    lab: string;
    clinic: string;
    note: string;
};

export type DentistLocalIds = {
    lab: string;
    clinic: string;
    localId: string;
};

/**
 * The lab's own record of which child clinic a dentist belongs to, inside a main
 * organization that has child clinics. Only ever contains the current lab's entries
 * (the backend filters by lab). Not related to the clinic-owned `defaultClinic`.
 */
export type LabDentistDefaultClinic = {
    mainClinic: string;
    defaultClinic: Organization;
};

export type User = {
    _id: string;
    id?: string; // For some reason this is needed for the resource API class that expects the @id parameter.
    syncid?: string;
    name: {
        firstName?: string;
        lastName?: string;
    };
    linkedOrganizations: LinkedOrganization[];
    fullName: string;
    firstName?: string;
    lastName?: string;
    title?: string;
    email: string;
    phone?: string;
    comments?: string;
    local?: boolean;
    localLabOwner?: string;
    client?: Organization | string;
    password?: string;
    role: string;
    active?: boolean;
    isShared?: boolean;
    type: string;
    lastLogin?: Date;
    /** DD-3613: set on the logged-in user while a super admin impersonates them. */
    impersonatedBy?: string;
    impersonatedByEmail?: string;
    profileImage?: any;
    isAdmin?: boolean;
    isDefaultClinic?: boolean;
    systemSetting?: {
        clinic?: any;
        autoArchiveOrderDays?: number;
    };
    localeFormat?: LocaleFormat;
    constructionSetting?: {
        ponticDesign?: string;
        metalDesign?: string;
        occlusionContact?: string;
        contactType?: string;
    };
    connectedLabs?: [
        {
            lab?: any;
        },
    ];
    organization?: Organization | string;
    emailNotifications?: {
        type?: string;
        otherEmail?: string;
        newMessage?: boolean;
        deliveryDateChanged?: boolean;
        orderReceivedByLab?: boolean;
        orderDeclinedByLab?: boolean;
        orderSentByLab?: boolean;
    };
    viewSettings?: {
        orderModified?: boolean;
        deliveryDatePassed?: boolean;
    };
    orderListSettings?: {
        selectedMainProfileId?: string | null;
        selectedWorkflowProfileId?: string | null;
        hasSelectedMainProfile?: boolean;
        hasSelectedWorkflowProfile?: boolean;
        columnWidths?: Array<{
            profileId: string;
            widths: Array<{ columnKey: string; width: number }>;
        }>;
    };
    generalNotifications?: UserGeneralNotifications;
    created?: {
        on?: number;
        by?: any;
    };
    updated?: {
        on?: number;
        by?: any;
    };
    permissions: {
        role: string;
        description: string;
        permissions: {
            [key: string]: boolean;
        };
        type: string;
        removed: boolean;
        active: boolean;
    };
    isTotpEnabled: boolean;
    personalIdentityNumber: string;
    signedUrl?: string;
    inputValue?: string;
    /** DD-3420: how this user authenticates — the single source of truth. */
    authMethod?: AuthMethod;
    whiteLabelAccess?: string[];
    dentistNotes?: DentistNotes[];
    dentistLocalIds?: DentistLocalIds[];
    labDentistDefaultClinics?: LabDentistDefaultClinic[];
    fullNameWithClinicInfo?: string;
    clinic?: Organization;
    defaultClinic?: Organization;
    dentistType?: DentistType;
    salesforceUser?: SalesforceUser;
    clinics?: Organization[];
    enableUserSync?: boolean;
};

export type Dentist = {
    _id?: string;
    title?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    organization?: Organization | string;
    parentClinic?: string;
    defaultClinic?: ClinicDropdown | string;
    isActive?: boolean;
    local?: boolean;
    linkedOrganizations?: LinkedOrganization[];
    authMethod?: AuthMethod;
    syncid?: string;
    fullNameWithClinicInfo?: string;
    dentistType?: DentistType;
    clinic?: Organization | string;
    dentistLocalIds?: DentistLocalIds[];
    labDentistDefaultClinics?: LabDentistDefaultClinic[];
    enableUserSync?: boolean;
    /** Only returned to Support and to the person themselves. */
    personalIdentityNumber?: string;
};

/**
 * DD-3420 — how a user authenticates. Mutually exclusive, and the single source of
 * truth: the frontend must not re-derive it from anything else, which is what made the
 * old flags unreliable.
 */
export enum AuthMethod {
    PASSWORD = 'PASSWORD',
    BANKID_SE = 'BANKID_SE',
    BANKID_NO = 'BANKID_NO',
    MITID_DK = 'MITID_DK',
    FTN_FI = 'FTN_FI',
    SITHS = 'SITHS',
    /** Signed in by the clinic's system through the integration endpoint (DD-3524). */
    INTEGRATION = 'INTEGRATION',
    SAML = 'SAML',
    OIDC = 'OIDC',
    NONE = 'NONE',
}

/** The eIDs Denthub brokers through Idura (DD-3513). */
export const IDURA_AUTH_METHODS = [AuthMethod.BANKID_SE, AuthMethod.BANKID_NO, AuthMethod.MITID_DK, AuthMethod.FTN_FI];

export const isIduraAuthMethod = (method?: AuthMethod): boolean => !!method && IDURA_AUTH_METHODS.includes(method);

/** Company SSO: the organisation's identity provider decides, and syncid is the subject it sends. */
export const COMPANY_SSO_AUTH_METHODS = [AuthMethod.SAML, AuthMethod.OIDC];

export const isCompanySsoAuthMethod = (method?: AuthMethod): boolean =>
    !!method && COMPANY_SSO_AUTH_METHODS.includes(method);

export type Technician = {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: string;
    organization?: Organization | string;
    active?: boolean;
    isShared?: boolean;
    linkedOrganizations?: LinkedOrganization[];
    salesforceUser?: SalesforceUser;
    authMethod?: AuthMethod;
    syncid?: string;
    enableUserSync?: boolean;
    /** Only returned to Support and to the person themselves. */
    personalIdentityNumber?: string;
};

export type LocalDentist = {
    _id: string;
    clinic: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    note?: string;
    localId?: string;
    active?: boolean;
    local?: boolean;
    convertToConnect?: boolean;
    labDefaultClinic?: string;
};

export type UserListFilter = {
    freeText: string;
    active: boolean | null;
    role?: string;
};

export type UserType = '' | 'dentist' | 'lab_technician';

export type Role = {
    _id: string;
    role: string;
};

export type ClinicDropdown = {
    _id: string;
    name: string;
    invoicePeriod?: string;
};

export type OrgDropdown = {
    _id: string;
    name: string;
};

export enum DentistType {
    ALL = 'ALL',
    MY_DENTIST = 'MY_DENTIST',
    DENTHUB_CONNECTED_DENTIST = 'DENTHUB_CONNECTED_DENTIST',
}

/**
 * DD-3513: the failure codes the backend may send back after an e-ID enrolment attempt.
 * Mirrors IDURA_ERROR_CODES in the backend; each is a key in the user-settings namespace.
 */
export const EID_ERROR_CODES = [
    'EID_CANCELLED',
    'EID_TIMEOUT',
    'EID_FAILED',
    'EID_ALREADY_IN_USE',
    'NO_FEDERATED_USER_FOUND',
    'BANK_ID_PERSONAL_NUMBER_MISMATCH',
] as const;
export type EidErrorCode = (typeof EID_ERROR_CODES)[number];
export const isEidErrorCode = (value: unknown): value is EidErrorCode =>
    typeof value === 'string' && (EID_ERROR_CODES as readonly string[]).includes(value);
